import { useNavigate, useLocation } from "react-router-dom";
import { Flame, Compass, CircleUser, Languages, Users, Sparkles } from "lucide-react";

function ProgressRing({ percent }) {
  const r = 26;
  const c = 2 * Math.PI * r;
  const off = c - (Math.min(100, Math.max(0, percent)) / 100) * c;
  return (
    <div className="relative h-16 w-16 shrink-0">
      <svg className="h-16 w-16 -rotate-90" viewBox="0 0 60 60">
        <circle cx="30" cy="30" r={r} className="stroke-secondary" strokeWidth="5" fill="none" />
        <circle
          cx="30" cy="30" r={r}
          className="stroke-primary transition-all duration-1000 ease-out"
          strokeWidth="5" fill="none" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={off}
        />
      </svg>
      <span data-testid="progress-percent" className="absolute inset-0 grid place-items-center text-sm font-bold">
        {percent}%
      </span>
    </div>
  );
}

const QUICK = [
  { label: "Explore AI", path: "/explore", icon: Compass, testid: "dash-explore" },
  { label: "My space", path: "/me", icon: CircleUser, testid: "dash-me" },
];

export function DashboardSidebar({ user, path, onShowLang, onCycleCreator, switching }) {
  const navigate = useNavigate();
  const location = useLocation();
  const initials = (user?.name || "U").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="flex flex-col gap-7">
      {/* profile */}
      <div className="flex items-center gap-3">
        <span className="grid place-items-center h-12 w-12 rounded-full bg-gradient-to-br from-indigo-100 to-pink-100 dark:from-indigo-900 dark:to-slate-800 text-indigo-700 dark:text-indigo-300 font-heading font-bold">
          {initials}
        </span>
        <div className="min-w-0">
          <p className="font-heading font-bold text-foreground truncate">{user?.name || "Learner"}</p>
          {path.archetype_label && (
            <span data-testid="archetype-chip" className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-secondary text-muted-foreground mt-0.5">
              {path.archetype_label}
            </span>
          )}
        </div>
      </div>

      {/* progress + streak */}
      <div className="flex items-center gap-4 p-4 rounded-2xl border border-border bg-card">
        <ProgressRing percent={path.percent} />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">{path.completed_count}/{path.total} lessons</p>
          <p className="text-xs text-muted-foreground">{path.finished ? "Path complete 🏆" : "Keep going!"}</p>
          <p data-testid="streak-badge" className="mt-1.5 flex items-center gap-1.5 text-sm font-medium text-foreground">
            <Flame className={`h-4 w-4 ${path.streak > 0 ? "text-orange-400 fill-orange-400" : "text-muted-foreground"}`} />
            {path.streak} day streak
          </p>
        </div>
      </div>

      {/* why this path — kept in the side rail so it never interrupts the lessons */}
      {path.why_summary && (
        <div data-testid="why-this-path" className="p-4 rounded-2xl border border-border bg-card">
          <p className="text-xs font-semibold text-muted-foreground mb-1.5 flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-primary" /> Why this path
          </p>
          <p data-testid="why-this-path-text" className="text-xs text-muted-foreground leading-relaxed">
            {path.why_summary}
          </p>
        </div>
      )}

      {/* preferences */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground mb-2">Preferences</p>
        <div className="flex flex-wrap gap-2">
          <button onClick={onShowLang} data-testid="change-language-chip"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-secondary text-muted-foreground hover:bg-accent hover:text-foreground transition-colors font-medium">
            <Languages className="h-3.5 w-3.5" /> {path.language_label}
          </button>
          <button onClick={onCycleCreator} disabled={switching} data-testid="creator-chip"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-secondary text-muted-foreground hover:bg-accent hover:text-foreground transition-colors font-medium disabled:opacity-60">
            <Users className="h-3.5 w-3.5" /> {path.creator_label}
          </button>
        </div>
      </div>

      {/* explore */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground mb-2">Explore</p>
        <nav className="flex flex-col gap-1">
          {QUICK.map((q) => {
            const Icon = q.icon;
            const active = location.pathname === q.path;
            return (
              <button key={q.path} data-testid={q.testid} onClick={() => navigate(q.path)}
                className={`group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-accent"
                }`}>
                <Icon className="h-4 w-4 opacity-70 group-hover:opacity-100 transition-opacity" /> {q.label}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
